// src/pages/AdminLogin.jsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [focused, setFocused] = useState("");
  const { adminLogin } = useAuth();
  const navigate = useNavigate();

  async function handleLogin() {
    if (!email || !password) return setError("Please fill all fields");
    try {
      setLoading(true);
      setError("");
      await adminLogin(email, password);
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.message === "Not an admin account"
        ? "⛔ This is not an admin account"
        : "❌ Invalid email or password");
    } finally {
      setLoading(false);
    }
  }

  const s = styles;
  return (
    <div className="login-page" style={s.page}>
      <div style={s.orb1} /><div style={s.orb2} /><div style={s.grid} />

      {/* Left Branding */}
      <div className="login-left" style={s.left}>
        <div style={s.logo}>
          <div style={{ display: "flex", alignItems: "center", background: "#fff", borderRadius: 10, padding: "8px 18px 8px 8px", gap: 12, boxShadow: "0 4px 16px rgba(0,0,0,0.2)" }}>
            <img src="/apdch_logo.png" alt="APDCH Logo" style={{ width: 72, height: 72, objectFit: "contain", filter: "drop-shadow(0 2px 6px rgba(0,0,0,0.15))" }} />
            <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <span style={{ fontWeight: 900, fontSize: 17, color: "#7b1a1a", letterSpacing: 0.3, lineHeight: 1.3 }}>
                Adhiparasakthi Dental College &amp; Hospital
              </span>
              <span style={{ fontWeight: 700, fontSize: 13, color: "#7b1a1a", letterSpacing: 2, opacity: 0.75 }}>
                MELMARUVATHUR
              </span>
            </div>
          </div>
        </div>
        <h1 style={s.headline}>Admin<br />Control Panel</h1>
        <p style={s.sub}>Manage faculty accounts, track academic achievements, and generate institution-wide reports.</p>
        <div style={s.features}>
          {[
            { icon: "👥", text: "Create & manage 90+ faculty accounts" },
            { icon: "📊", text: "View all publications, awards & data" },
            { icon: "📥", text: "Export reports & analytics" },
          ].map((f, i) => (
            <div key={i} style={s.featureItem}>
              <span style={{ fontSize: 22 }}>{f.icon}</span>
              <span style={s.featureText}>{f.text}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="login-divider" style={s.divider} />

      {/* Right Form */}
      <div className="login-right" style={s.right}>
        <div style={s.card}>
          <div style={s.badge}>
            <div style={s.badgeDot} />
            <span style={s.badgeText}>ADMIN ACCESS ONLY</span>
          </div>
          <h2 style={s.formTitle}>Welcome Back</h2>
          <p style={s.formSub}>Sign in to your admin account</p>

          {error && <div style={s.error}>{error}</div>}

          {localStorage.getItem("session_terminated_debug") && (
            <div style={{ ...s.error, background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", color: "#fbbf24" }}>
              ⚠️ Debug Info: {localStorage.getItem("session_terminated_debug")}
              <button 
                onClick={() => { localStorage.removeItem("session_terminated_debug"); window.location.reload(); }}
                style={{ background: "transparent", border: "none", color: "#60a5fa", cursor: "pointer", marginLeft: 10, textDecoration: "underline", fontWeight: 700 }}
              >
                Clear
              </button>
            </div>
          )}

          {/* Email */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Email Address</label>
            <div style={{ ...s.inputWrap, ...(focused === "email" ? s.inputFocused : {}) }}>
              <span style={s.inputIcon}>✉️</span>
              <input
                type="email" placeholder="admin@university.edu" value={email}
                onChange={e => setEmail(e.target.value)}
                onFocus={() => setFocused("email")} onBlur={() => setFocused("")}
                style={s.input}
              />
            </div>
          </div>

          {/* Password */}
          <div style={s.fieldGroup}>
            <label style={s.label}>Password</label>
            <div style={{ ...s.inputWrap, ...(focused === "pass" ? s.inputFocused : {}) }}>
              <span style={s.inputIcon}>🔒</span>
              <input
                type={showPass ? "text" : "password"} placeholder="Enter your password" value={password}
                onChange={e => setPassword(e.target.value)}
                onFocus={() => setFocused("pass")} onBlur={() => setFocused("")}
                onKeyDown={e => e.key === "Enter" && handleLogin()}
                style={s.input}
              />
              <span onClick={() => setShowPass(v => !v)} style={s.eyeIcon}>{showPass ? "🙈" : "👁️"}</span>
            </div>
          </div>

          <div style={{ textAlign: "right", marginBottom: 28 }}>
            <span style={s.forgotLink} onClick={async () => {
  const emailVal = email.trim();
  if (!emailVal) return alert("Please enter your email first");
  try {
    const res = await fetch("/api/auth/forgot-password", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: emailVal })
    });
    const data = await res.json();
    if (res.ok) alert("? Reset email sent! Check your inbox.");
    else alert("? " + (data.error || "Failed"));
  } catch {
    alert("? Server error");
  }
}}>Forgot password?</span>
          </div>

          <button onClick={handleLogin} disabled={loading} style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }}>
            {loading ? "⏳ Signing in..." : "🔐 Sign In as Admin"}
          </button>

          <div style={s.orRow}>
            <div style={s.orLine} /><span style={s.orText}>OR</span><div style={s.orLine} />
          </div>

          <div style={s.switchBox}>
            <span style={s.switchText}>Are you a faculty member? </span>
            <span onClick={() => navigate("/login")} style={s.switchLink}>Faculty Login →</span>
          </div>
        </div>
        <p style={s.footer}>🔒 Secure access · Only authorized administrators</p>
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "linear-gradient(135deg, #060d1f 0%, #0f2557 50%, #1a1060 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Nunito', sans-serif", position: "relative", overflow: "hidden" },
  orb1: { position: "absolute", width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)", top: -100, left: -100, pointerEvents: "none" },
  orb2: { position: "absolute", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(139,92,246,0.12) 0%, transparent 70%)", bottom: -80, right: -80, pointerEvents: "none" },
  grid: { position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)", backgroundSize: "50px 50px", pointerEvents: "none" },
  left: { flex: 1, maxWidth: 480, padding: "60px 48px", color: "#fff", display: "flex", flexDirection: "column", justifyContent: "center" },
  logo: { display: "flex", alignItems: "center", gap: 12, marginBottom: 48 },
  logoIcon: { width: 48, height: 48, background: "linear-gradient(135deg, #2563eb, #7c3aed)", borderRadius: 14, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, boxShadow: "0 0 20px rgba(37,99,235,0.4)" },
  logoText: { fontSize: 28, fontWeight: 900, letterSpacing: 1 },
  headline: { fontSize: 44, fontWeight: 900, lineHeight: 1.15, marginBottom: 20, background: "linear-gradient(90deg, #fff 0%, #93c5fd 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" },
  sub: { color: "rgba(255,255,255,0.55)", fontSize: 18, lineHeight: 1.7, maxWidth: 340 },
  features: { display: "flex", flexDirection: "column", gap: 14, marginTop: 40 },
  featureItem: { display: "flex", alignItems: "center", gap: 14, background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, padding: "12px 18px" },
  featureText: { color: "rgba(255,255,255,0.75)", fontSize: 16 },
  divider: { width: 1, height: 420, background: "linear-gradient(to bottom, transparent, rgba(255,255,255,0.12), transparent)", margin: "0 20px", flexShrink: 0 },
  right: { flex: 1, maxWidth: 420, padding: "48px" },
  card: { background: "rgba(255,255,255,0.05)", backdropFilter: "blur(20px)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 24, padding: "40px 36px", boxShadow: "0 32px 64px rgba(0,0,0,0.4)" },
  badge: { display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 20, padding: "4px 14px", marginBottom: 28 },
  badgeDot: { width: 7, height: 7, borderRadius: "50%", background: "#ef4444" },
  badgeText: { color: "#fca5a5", fontSize: 14, fontWeight: 700, letterSpacing: 1 },
  formTitle: { color: "#fff", fontSize: 28, fontWeight: 800, marginBottom: 6 },
  formSub: { color: "rgba(255,255,255,0.45)", fontSize: 16, marginBottom: 28 },
  error: { background: "rgba(239,68,68,0.15)", border: "1px solid rgba(239,68,68,0.3)", borderRadius: 10, padding: "10px 14px", color: "#fca5a5", fontSize: 15, marginBottom: 18 },
  fieldGroup: { marginBottom: 18 },
  label: { color: "rgba(255,255,255,0.7)", fontSize: 15, fontWeight: 600, display: "block", marginBottom: 8 },
  inputWrap: { display: "flex", alignItems: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 12, padding: "0 16px", transition: "all 0.2s" },
  inputFocused: { background: "rgba(37,99,235,0.12)", border: "1px solid rgba(37,99,235,0.6)" },
  inputIcon: { color: "rgba(255,255,255,0.4)", fontSize: 19, marginRight: 10 },
  input: { flex: 1, background: "transparent", border: "none", outline: "none", color: "#fff", fontSize: 17, padding: "14px 0", fontFamily: "'Nunito', sans-serif" },
  eyeIcon: { color: "rgba(255,255,255,0.4)", cursor: "pointer", fontSize: 18 },
  forgotLink: { color: "#93c5fd", fontSize: 15, cursor: "pointer", fontWeight: 600 },
  btn: { width: "100%", background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)", border: "none", borderRadius: 12, padding: "15px", color: "#fff", fontSize: 18, fontWeight: 800, cursor: "pointer", letterSpacing: 0.5, boxShadow: "0 4px 24px rgba(37,99,235,0.4)", fontFamily: "'Nunito', sans-serif" },
  btnDisabled: { background: "rgba(37,99,235,0.5)", boxShadow: "none", cursor: "not-allowed" },
  orRow: { display: "flex", alignItems: "center", gap: 12, margin: "24px 0" },
  orLine: { flex: 1, height: 1, background: "rgba(255,255,255,0.1)" },
  orText: { color: "rgba(255,255,255,0.3)", fontSize: 14 },
  switchBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: "14px", textAlign: "center" },
  switchText: { color: "rgba(255,255,255,0.5)", fontSize: 15 },
  switchLink: { color: "#93c5fd", fontWeight: 700, cursor: "pointer", fontSize: 15 },
  footer: { color: "rgba(255,255,255,0.25)", fontSize: 14, textAlign: "center", marginTop: 20, lineHeight: 1.6 },
};
